// AGC: LuckyNumberReveal - slot machine animation that reveals a 5-digit lucky number
// AGC: Digits spin left-to-right stopping one per second; calls onComplete after all revealed

import React, { useState, useEffect, useRef, useCallback } from 'react'

interface LuckyNumberRevealProps {
  /** AGC: 5-digit string e.g. "04287" */
  number: string
  onComplete: () => void
}

const DIGIT_COUNT = 5
const SPIN_INTERVAL_MS = 50
const FIRST_STOP_DELAY_MS = 1500
const STOP_GAP_MS = 1000
const COMPLETE_DELAY_MS = 500

export const LuckyNumberReveal: React.FC<LuckyNumberRevealProps> = ({ number, onComplete }) => {
  const target = number.padStart(DIGIT_COUNT, '0').slice(0, DIGIT_COUNT)

  // AGC: Use refs for mutable state that the interval needs to read
  const displayRef = useRef<string[]>(
    Array.from({ length: DIGIT_COUNT }, () => String(Math.floor(Math.random() * 10)))
  )
  const revealedRef = useRef<boolean[]>(Array(DIGIT_COUNT).fill(false))
  const [, forceRender] = useState(0)

  // AGC: Force a re-render to show updated digits
  const tick = useCallback(() => forceRender(n => n + 1), [])

  useEffect(() => {
    // AGC: Spin interval — update all still-spinning digits with random values
    const spinInterval = setInterval(() => {
      let changed = false
      for (let i = 0; i < DIGIT_COUNT; i++) {
        if (!revealedRef.current[i]) {
          displayRef.current[i] = String(Math.floor(Math.random() * 10))
          changed = true
        }
      }
      if (changed) tick()
    }, SPIN_INTERVAL_MS)

    // AGC: Schedule stop events for each digit left-to-right
    const timeouts: ReturnType<typeof setTimeout>[] = []

    for (let i = 0; i < DIGIT_COUNT; i++) {
      const delay = FIRST_STOP_DELAY_MS + i * STOP_GAP_MS
      const t = setTimeout(() => {
        // AGC: Lock this digit to its real value
        displayRef.current[i] = target[i]
        revealedRef.current[i] = true
        tick()

        // AGC: After the last digit stops, wait briefly then notify parent
        const doneCount = revealedRef.current.filter(Boolean).length
        if (doneCount === DIGIT_COUNT) {
          clearInterval(spinInterval)
          const ct = setTimeout(onComplete, COMPLETE_DELAY_MS)
          timeouts.push(ct)
        }
      }, delay)
      timeouts.push(t)
    }

    return () => {
      clearInterval(spinInterval)
      timeouts.forEach(clearTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: DIGIT_COUNT }, (_, i) => {
        const isRevealed = revealedRef.current[i]
        return (
          <div
            key={i}
            className={[
              'flex h-20 w-14 items-center justify-center rounded-xl text-4xl font-bold transition-all duration-300',
              isRevealed
                ? 'bg-yellow-500 text-black shadow-lg shadow-yellow-500/40 scale-105'
                : 'bg-gray-700 text-white',
            ].join(' ')}
          >
            {displayRef.current[i]}
          </div>
        )
      })}
    </div>
  )
}

export default LuckyNumberReveal
